import { closeOfferModalSelector, hideCloseOfferModal } from '@/features/offers/slices/offerModal.slice.js'
import { UserRole } from '@/features/users/constants'
import { authSelector } from '@/stores/auth/authSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { createOptions } from '@/utils/arrayHelper.js'
import { camelToWords } from '@/utils/stringHelper.js'
import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useReason } from '../../../../reasons/useReason.js'
import useOffer from '../../../hooks/useOffer.js'
import useOfferAction from '../../../hooks/useOfferAction.js'

const CloseOfferModal = () => {
  const { show, offerId, type } = useSelector(closeOfferModalSelector)

  const [selectedMainReasonId, setSelectedMainReasonId] = useState('')
  const [subReason, setSubReason] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const dispatch = useDispatch()

  const { useFetchReasonsByType } = useReason()
  const { data: fetchReasonResponse } = useFetchReasonsByType(type)
  const allReasons = fetchReasonResponse ?? []

  const { useCloseOffer } = useOffer()
  const closeOffer = useCloseOffer()

  const { useToggleOfferRenewalLost } = useOfferAction()
  const toggleOfferRenewalLost = useToggleOfferRenewalLost()

  //Transformed

  const mainReasonOptions = createOptions(allReasons, (reason) => camelToWords(reason?.name), '_id')
  const selectedMainReason = allReasons.find((reason) => reason?._id === selectedMainReasonId)

  const subReasonOptions = Array.isArray(selectedMainReason?.subOptions) ? selectedMainReason.subOptions : []

  const handleModalClose = useCallback(() => {
    setSelectedMainReasonId('')
    setSubReason('')
    setError('')
    dispatch(hideCloseOfferModal())
  }, [dispatch])

  const handleMainReasonChange = (e) => {
    setSelectedMainReasonId(e.target.value)
    setSubReason('')
  }

  const handleSubReasonChange = (e) => {
    setSubReason(e.target.value)
    setError('')
  }

  const handleConfirm = useCallback(async () => {
    console.log(subReason, selectedMainReasonId)
    if (selectedMainReasonId === '' || subReason === '') {
      setError('Please input both main reason and sub reason')
      return
    }

    try {
      setIsSubmitting(true)
      dispatch(showLoadingOverlay())

      if (type === 'maintenance') {
        await toggleOfferRenewalLost.mutateAsync({
          offerId,
          data: {
            maintenanceLostReason: selectedMainReasonId,
            maintenanceLostDetailedReason: subReason,
          },
        })
      } else {
        await closeOffer.mutateAsync({
          offerId,
          data: {
            offerCloseMainReason: selectedMainReasonId,
            offerCloseDetailedReason: subReason,
          },
        })
      }

      handleModalClose()
      setError('')
    } catch (error) {
      console.error('Failed to update offer:', error)
      setError('Failed to update offer. Please try again.')
    } finally {
      setIsSubmitting(false)
      dispatch(hideLoadingOverlay())
    }
  }, [dispatch, handleModalClose, offerId, selectedMainReason, subReason])
  const { user: currentUser } = useSelector(authSelector)

  const isUserAdmin = currentUser.role === UserRole.ADMIN

  return (
    <Modal isOpen={show} onClose={handleModalClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{type === 'offer' ? 'Close Offer' : 'Renewal Lost'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={2}>Main Reason</Text>
              <Select value={selectedMainReasonId} onChange={handleMainReasonChange} placeholder="Select">
                {mainReasonOptions
                  .filter(
                    (option) =>
                      !(option.label.toLowerCase() === 'delete' && !isUserAdmin) &&
                      option.label.toLowerCase() != 'project awarded to another customer'
                  )
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </Select>
            </Box>

            <Box>
              <Text mb={2}>Sub Reason</Text>
              {type === 'offer' ? (
                <Select
                  value={subReason}
                  onChange={handleSubReasonChange}
                  isDisabled={!selectedMainReason}
                  placeholder="Select"
                >
                  {subReasonOptions.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  type="text"
                  value={subReason}
                  onChange={handleSubReasonChange}
                  isDisabled={!selectedMainReason}
                />
              )}
            </Box>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleModalClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleConfirm} isLoading={isSubmitting} loadingText="Confirming">
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CloseOfferModal
